import { createContext, useState, useContext, ReactNode, useMemo, useCallback, useEffect } from 'react'
import { parseDate } from '@internationalized/date'
import { RangeValue } from '@react-types/shared'
import { DateValue } from '@react-types/calendar'
import { Country } from '../models'
import { START, END } from '../constants'
import { IConfigSeller } from '@/models/configSeller'
import { IUser } from '@/models/user.model'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { webApiService } from '@/services'

interface DashboardContextType {
  country: Country
  dateRange: RangeValue<DateValue>
  isLoading: boolean
  selectedSeller: number | null
  setDateRange: (dateRange: RangeValue<DateValue>) => void
  incrementLoadingCards: () => void
  decrementLoadingCards: () => void
  showRegionFilters: boolean
  isTiRegion: boolean
  regions: any[]
  stores: any[]
  selectedRegion: string
  selectedStore: string
  handleRegionChange: (region: string) => void
  setSelectedStore: (store: string) => void
}

const DashboardContext = createContext<DashboardContextType | null>(null)

export function DashboardProvider ({ children }: { children: ReactNode }) {
  const configSeller = useSelector((store: { config: IConfigSeller }) => store.config)
  const userState = useSelector((store: { user: IUser }) => store.user)
  const { slug } = useParams()
  const country = import.meta.env.VITE_COUNTRY
  const selectedSeller = configSeller.config ? configSeller.config.idSeller : null
  const [loadingCards, setLoadingCards] = useState<number>(0)
  const [dateRange, setDateRange] = useState<RangeValue<DateValue>>({
    start: parseDate(START),
    end: parseDate(END)
  })
  const isLoading = loadingCards > 0
  const incrementLoadingCards = useCallback(() => setLoadingCards(prev => prev + 1), [])
  const decrementLoadingCards = useCallback(() => setLoadingCards(prev => Math.max(0, prev - 1)), [])

  const [regions, setRegions] = useState<any[]>([])
  const [stores, setStores] = useState<any[]>([])
  const [selectedRegion, setSelectedRegion] = useState('')
  const [selectedStore, setSelectedStore] = useState('')

  const isCoppelMexico = configSeller.config?.s3?.FOLDER_APP === 'ti_coppel'
  const isTiRegion = userState.id_role === Number(import.meta.env.VITE_PERFIL_TI_REGION)
  const isAdmin = userState.id_role === Number(import.meta.env.VITE_PERFIL_ADMIN)
  const showRegionFilters = isCoppelMexico && (isAdmin || isTiRegion)

  const loadRegions = useCallback(async () => {
    await webApiService.getStoreRegions(slug).then((response) => {
      setRegions(response.data ?? response ?? [])
    })
  }, [slug])

  const handleRegionChange = useCallback(async (region: string) => {
    setSelectedRegion(region)
    setSelectedStore('')
    setStores([])
    if (!region) return
    await webApiService.getStoresByRegion(slug, region).then((response) => {
      setStores(response.data ?? response ?? [])
    })
  }, [slug])

  const loadUserRegion = useCallback(async () => {
    await webApiService.getRegionByUser(userState.id).then((response) => {
      const raw = response.data ?? response
      const reg = Array.isArray(raw) ? raw[0] : raw
      const value = reg == null ? '' : String(reg.region ?? reg.name ?? reg)
      if (value) handleRegionChange(value)
    })
  }, [userState.id, handleRegionChange])

  useEffect(() => {
    if (!isCoppelMexico) return
    loadRegions()
    if (isTiRegion) loadUserRegion()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const value = useMemo(
    () => ({
      country,
      dateRange,
      isLoading,
      selectedSeller,
      setDateRange,
      incrementLoadingCards,
      decrementLoadingCards,
      showRegionFilters,
      isTiRegion,
      regions,
      stores,
      selectedRegion,
      selectedStore,
      handleRegionChange,
      setSelectedStore
    }),
    [country, dateRange, isLoading, selectedSeller, incrementLoadingCards, decrementLoadingCards, showRegionFilters, isTiRegion, regions, stores, selectedRegion, selectedStore, handleRegionChange]
  )

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  )
}

export function useDashboard () {
  const context = useContext(DashboardContext)
  if (!context) {
    throw new Error('useDashboard must be used within a DashboardProvider')
  }
  return context
}
